import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DataService } from '../../services/data.service'; // Importa il servizio
import { WaybillService } from '../../services/waybill.service'; // Importa il servizio waybill
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Waybill, Parcel, ParcelStatus, WaybillStatus, WaybillDeliveryMethod, WaybillDeliveryType } from '../../interfaces/waybill.interfaces';

@Component({
  selector: 'app-details',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './details.component.html',
  styleUrl: './details.component.css'
})
export class DetailsComponent implements OnInit {
  manifestDetail: any;
  expandedWaybills = new Set<string>();
  isLoading = true;
  waybills: Waybill[] = [];
  filteredWaybills: Waybill[] = [];
  currentManifestCode: string = '';
  searchQuery: string = '';
  
  // Map to store loaded parcels for each waybill
  waybillParcels = new Map<string, Parcel[]>();
  loadingParcels = new Set<string>();

  constructor(
    private route: ActivatedRoute, 
    private dataService: DataService,
    private waybillService: WaybillService,
    private router: Router,
    private location: Location
  ) {}
  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const manifestCode = params.get('code');
      if (manifestCode) {
        this.currentManifestCode = manifestCode;
        console.log('Manifest Code:', manifestCode);
    
        // Get basic manifest details from data service
        const manifestDetails = this.dataService.getData<any[]>('manifest_details');
        this.manifestDetail = manifestDetails?.find(m => m.manifestCode === manifestCode);
    
        console.log('Manifest Detail:', this.manifestDetail);
    
        const manifest = this.dataService.getData<any[]>('manifests')?.find(m => m.code === manifestCode);
        console.log('Manifest:', manifest);
        
        // Get enhanced waybill data from waybill service
        this.loadWaybills(manifestCode);
      } else {
        console.error('No manifest code provided');
        this.isLoading = false;
      }
    });
  }
  
  /**
   * Load waybills data for the current manifest
   * @param manifestCode The code of the manifest
   */  private loadWaybills(manifestCode: string): void {
    this.waybillService.getWaybillsByManifest(manifestCode).subscribe({
      next: (waybills) => {
        this.waybills = waybills;
        this.filteredWaybills = [...waybills];
        console.log('Enhanced waybills loaded:', waybills);
        
        // Update the manifest detail waybills with enhanced data
        if (this.manifestDetail) {
          this.manifestDetail.waybills = this.filteredWaybills;
        }
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading waybills:', error);
        this.isLoading = false;
      }
    });
  }
    /**
   * Filter waybills based on search query
   * Searches through all waybill properties and associated parcels
   */
  filterWaybills(): void {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredWaybills = [...this.waybills];
    } else {
      const query = this.searchQuery.toLowerCase().trim();
      
      this.filteredWaybills = this.waybills.filter(waybill => {
        // Check waybill properties
        if (this.matchesWaybillProperties(waybill, query)) {
          return true;
        }
        
        // Check parcels from waybill service (since parcels are not directly in waybill anymore)
        // For now, we'll search only in waybill properties
        // In a future version, we could get parcels separately and search through them
        
        return false;
      });
    }
    
    if (this.manifestDetail) {
      this.manifestDetail.waybills = this.filteredWaybills;
    }
  }
  /**
   * Check if any waybill property matches the search query
   */
  private matchesWaybillProperties(waybill: Waybill, query: string): boolean {
    return !!(
      (waybill.waybillCode && waybill.waybillCode.toLowerCase().includes(query)) ||
      (waybill.waybillShipmentCode && waybill.waybillShipmentCode.toLowerCase().includes(query)) ||
      (waybill.waybillPalletCode && waybill.waybillPalletCode.toLowerCase().includes(query)) ||
      (waybill.waybillTrackingNumber && waybill.waybillTrackingNumber.toLowerCase().includes(query)) ||
      (waybill.waybillOriginLocation && waybill.waybillOriginLocation.toLowerCase().includes(query)) ||
      (waybill.waybillDestinationLocation && waybill.waybillDestinationLocation.toLowerCase().includes(query)) ||
      (waybill.waybillLastKnownLocation && waybill.waybillLastKnownLocation.toLowerCase().includes(query)) ||
      (waybill.waybillNote && waybill.waybillNote.toLowerCase().includes(query)) ||
      (waybill.waybillStatus && waybill.waybillStatus.toLowerCase().includes(query)) ||
      (waybill.waybillDeliveryMethod && waybill.waybillDeliveryMethod.toLowerCase().includes(query)) ||
      (waybill.waybillDeliveryType && waybill.waybillDeliveryType.toLowerCase().includes(query)) ||
      (waybill.waybillBrandCode && waybill.waybillBrandCode.toLowerCase().includes(query)) ||
      (waybill.waybillDealerCode && waybill.waybillDealerCode.toLowerCase().includes(query)) ||
      (waybill.waybillCarrierCode && waybill.waybillCarrierCode.toLowerCase().includes(query)) ||
      (waybill.waybillUpdatedBy && waybill.waybillUpdatedBy.toLowerCase().includes(query))
    );
  }
    /**
   * Check if any parcel property matches the search query
   */
  private matchesParcelProperties(parcel: Parcel, query: string): boolean {    return !!(
      (parcel.parcelCode && parcel.parcelCode.toLowerCase().includes(query)) ||
      (parcel.parcelWeight && parcel.parcelWeight.toString().includes(query)) ||
      (parcel.parcelLength && parcel.parcelLength.toString().includes(query)) ||
      (parcel.parcelWidth && parcel.parcelWidth.toString().includes(query)) ||
      (parcel.parcelHeight && parcel.parcelHeight.toString().includes(query)) ||
      (parcel.parcelType && parcel.parcelType.toLowerCase().includes(query)) ||
      (parcel.parcelStatus && parcel.parcelStatus.toString().toLowerCase().includes(query)) ||
      (parcel.parcelDescription && parcel.parcelDescription?.toLowerCase().includes(query)) ||
      (parcel.parcelTrackingNumber && parcel.parcelTrackingNumber.toLowerCase().includes(query)) ||
      (parcel.parcelShipmentCode && parcel.parcelShipmentCode.toLowerCase().includes(query))
    );
  }

  goBack(): void {
    this.location.back();
  }  toggleWaybill(waybillCode: string): void {
    if (this.expandedWaybills.has(waybillCode)) {
      this.expandedWaybills.delete(waybillCode);
    } else {
      this.expandedWaybills.add(waybillCode);
    }
  }

  printManifest(): void {
    window.print();
  }

  emailManifest(): void {
    // TODO: Implement email functionality
    console.log('Email manifest functionality');
  }
  configureManifest(): void {
    // TODO: Implement configuration functionality
    console.log('Configure manifest functionality');
  }
  addWaybill(): void {
    // TODO: Implement add waybill functionality
    console.log('Add waybill functionality');
  }  /**
   * Remove a parcel from a waybill
   * @param waybill The waybill object
   * @param parcelCode The parcel code to remove
   * @param event Mouse event
   */
  removeParcel(waybill: Waybill, parcelCode: string, event: MouseEvent): void {
    // Prevent the click from propagating to the parent elements
    event.stopPropagation();
    
    const waybillCode = waybill.waybillCode;
    
    if (confirm(`Sei sicuro di voler rimuovere il collo ${parcelCode}?`)) {
      const removed = this.waybillService.removeParcel(waybillCode, parcelCode);
      
      if (removed) {
        console.log(`Parcel ${parcelCode} removed successfully`);
        
        // Log the action
        this.waybillService.logParcelAction(waybillCode, parcelCode, 'remove', 'User initiated removal');
        
        // Refresh waybills data
        this.loadWaybills(this.currentManifestCode);
      } else {
        console.error(`Failed to remove parcel ${parcelCode}`);
      }
    }
  }

  /**
   * Relabel a parcel with new information
   * @param waybill The waybill object
   * @param parcelCode The parcel code to relabel
   * @param event Mouse event
   */  relabelParcel(waybill: Waybill, parcelCode: string, event: MouseEvent): void {
    // Prevent the click from propagating to the parent elements
    event.stopPropagation();
      const waybillCode = waybill.waybillCode;
    
    // In a real app, this would open a dialog to enter new label details
    // For now, we'll simulate with a simple prompt
    const newType = prompt(`Inserisci un nuovo tipo per il collo ${parcelCode}:`, 'STANDARD');
    
    if (newType !== null) {
      const updated = this.waybillService.relabelParcel(waybillCode, parcelCode, { 
        parcelType: newType,
        parcelUpdatedAt: new Date().toISOString()
      });
      
      if (updated) {
        console.log(`Parcel ${parcelCode} relabeled successfully`);
        
        // Log the action
        this.waybillService.logParcelAction(waybillCode, parcelCode, 'relabel', `Type changed to ${newType}`);
        
        // Refresh waybills data
        this.loadWaybills(this.currentManifestCode);
      } else {
        console.error(`Failed to relabel parcel ${parcelCode}`);
      }
    }
  }

  /**
   * Get waybill display code (use waybillCode as primary identifier)
   */
  getWaybillDisplayCode(waybill: Waybill): string {
    return waybill.waybillCode || 'N/A';
  }

  /**
   * Get waybill destination for display
   */
  getWaybillDestination(waybill: Waybill): string {
    return waybill.waybillDestinationLocation || waybill.waybillLastKnownLocation || 'Unknown';
  }

  /**
   * Get waybill origin for display
   */
  getWaybillOrigin(waybill: Waybill): string {
    return waybill.waybillOriginLocation || 'Unknown';
  }

  /**
   * Check if waybill is fragile
   */
  isWaybillFragile(waybill: Waybill): boolean {
    return waybill.waybillFragile || false;
  }

  /**
   * Check if waybill contains hazardous material
   */
  hasHazardousMaterial(waybill: Waybill): boolean {
    return waybill.waybillHazardousMaterial || false;
  }

  /**
   * Check if customs clearance is required
   */
  requiresCustomsClearance(waybill: Waybill): boolean {
    return waybill.waybillCustomsClearanceRequired || false;
  }

  /**
   * Get waybill delivery method display text
   */
  getDeliveryMethodDisplay(waybill: Waybill): string {
    return waybill.waybillDeliveryMethod || 'Standard';
  }

  /**
   * Get waybill total weight for display
   */
  getTotalWeight(waybill: Waybill): string {
    return waybill.waybillTotalWeight ? `${waybill.waybillTotalWeight} kg` : 'N/A';
  }

  /**
   * Get waybill total volume for display
   */
  getTotalVolume(waybill: Waybill): string {
    return waybill.waybillTotalVolume ? `${waybill.waybillTotalVolume} m³` : 'N/A';
  }

  /**
   * Get waybill status display class for styling
   */
  getWaybillStatusClass(waybill: Waybill): string {
    const status = waybill.waybillStatus?.toLowerCase();
    switch (status) {
      case 'active':
        return 'badge bg-success';
      case 'inactive':
        return 'badge bg-secondary';
      case 'in_transit':
        return 'badge bg-primary';
      case 'delivered':
        return 'badge bg-success';
      case 'problem':
        return 'badge bg-danger';
      default:
        return 'badge bg-info';
    }
  }

  /**
   * Load and display parcels for a specific waybill
   * @param waybillCode The waybill code
   */
  async loadWaybillParcels(waybillCode: string): Promise<void> {
    if (this.loadingParcels.has(waybillCode)) {
      return; // Already loading
    }

    this.loadingParcels.add(waybillCode);
    
    try {
      const parcels = await this.getWaybillParcels(waybillCode);
      this.waybillParcels.set(waybillCode, parcels);
      console.log(`Loaded ${parcels.length} parcels for waybill ${waybillCode}`);
    } catch (error) {
      console.error('Error loading parcels for waybill:', waybillCode, error);
    } finally {
      this.loadingParcels.delete(waybillCode);
    }
  }

  /**
   * Get parcels for a waybill from cache or load them
   * @param waybillCode The waybill code
   * @returns Array of parcels or null if not loaded
   */
  getWaybillParcelsFromCache(waybillCode: string): Parcel[] | null {
    return this.waybillParcels.get(waybillCode) || null;
  }

  /**
   * Check if parcels are being loaded for a waybill
   * @param waybillCode The waybill code
   * @returns True if loading
   */
  isParcelsLoading(waybillCode: string): boolean {
    return this.loadingParcels.has(waybillCode);
  }

  /**
   * Check if parcels are loaded for a waybill
   * @param waybillCode The waybill code
   * @returns True if loaded
   */
  areParcelsLoaded(waybillCode: string): boolean {
    return this.waybillParcels.has(waybillCode);
  }

  /**
   * Get waybill parcels, loading them if not already loaded
   * @param waybillCode The waybill code
   * @returns Promise of parcels array
   */
  async getWaybillParcels(waybillCode: string): Promise<Parcel[]> {
    return new Promise((resolve) => {
      this.waybillService.getParcelsByWaybill(waybillCode).subscribe({
        next: (parcels) => resolve(parcels),
        error: (error) => {
          console.error('Error loading parcels:', error);
          resolve([]);
        }
      });
    });
  }

  /**
   * Enhanced filter method that includes parcel search
   */
  async filterWaybillsWithParcels(): Promise<void> {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredWaybills = [...this.waybills];
    } else {
      const query = this.searchQuery.toLowerCase().trim();
      const matchingWaybills: Waybill[] = [];
      
      for (const waybill of this.waybills) {
        // Check waybill properties first
        if (this.matchesWaybillProperties(waybill, query)) {
          matchingWaybills.push(waybill);
          continue;
        }
        
        // Check parcels for this waybill
        try {
          const parcels = await this.getWaybillParcels(waybill.waybillCode);
          if (parcels.some(parcel => this.matchesParcelProperties(parcel, query))) {
            matchingWaybills.push(waybill);
          }
        } catch (error) {
          console.error('Error searching parcels for waybill:', waybill.waybillCode, error);
        }
      }
      
      this.filteredWaybills = matchingWaybills;
    }
    
    if (this.manifestDetail) {
      this.manifestDetail.waybills = this.filteredWaybills;
    }
  }
}
